import { Component, OnDestroy, OnInit } from '@angular/core';
import { _ } from '~app/shared/utils';
import { interval, Subscription } from 'rxjs';
import { Observable, from } from 'rxjs';
import { concatMap, map, reduce, startWith, switchMap, tap } from 'rxjs/operators';
import { getPowerInformationTagDefinitions, TagPair } from '~app/automation/tag-definitions/T14';
import { LoadingConfig } from '~app/components/qm-directives/loading.directive';
import { NormalizedTagsResult, NormalizedTrendResult, ValmetDnaService, ValmetDnaTag, ValmetDnaValue } from '~app/custom/neve/valmet-dna.service';
import { rateLimit } from '~app/shared/utils';

interface TableData {
  rows: {
    formattedValue?: string,
    valmetDnaValue?: ValmetDnaValue,
    valmetDnaWrappedTagsResult: WrappedTagsResult
  }[],
  totalValueSum: string
}

type RequestedTag = {
  mainTag: string
  subTag: string
  fullTag: string
};

type WrappedTagsResult = {
  tagsResult?: NormalizedTagsResult
  requestedTag: RequestedTag
  tagFound: boolean,
  tag?: ValmetDnaTag
}

type CombinedResults = {
  tagsResult: WrappedTagsResult
  trendResult?: NormalizedTrendResult
}

@Component({
  selector: 'qm-neve-measurements-table',
  templateUrl: './neve-measurements-table.component.html',
  styleUrls: ['./neve-measurements-table.component.scss']
})
export class NeveMeasurementsTableComponent implements OnInit, OnDestroy {
  public tableData$: Observable<TableData>;
  public loading: boolean;
  public loadingConfig: LoadingConfig = {
    timeout: 15,
    messages: {
      empty: _('Power data is currently not available'),
    }
  };
  private tags: TagPair[];
  private tagResults: WrappedTagsResult[];

  private subs: Subscription[] = [];
  private readonly RATE_LIMIT_MS = 500;
  private readonly REFRESH_RATE_MS = 60 * 1000;

  constructor(private valmetDna: ValmetDnaService) {
    this.tags = getPowerInformationTagDefinitions();
  }

  ngOnDestroy(): void {
    for(const sub of this.subs) {
      sub.unsubscribe();
    }
  }

  async ngOnInit() {
    this.loading = true;
    this.tagResults = await this.getTagsSource().toPromise();
    const refreshEvents$ = interval(this.REFRESH_RATE_MS).pipe(
      startWith(0)
    );
    const valuesSource$ = this.getValuesSource(this.tagResults);
    this.tableData$ = this.fetchTrendResults(valuesSource$, refreshEvents$);
  }

  private fetchTrendResults(valuesSource$: Observable<CombinedResults[]>, refreshEvents$: Observable<number>) {
    this.subs.push(
      refreshEvents$.subscribe({
        next: () => this.loading = true
      })
    );

    const tableData$ = refreshEvents$.pipe(
      switchMap(() => valuesSource$),
      map(results => this.getTableData(results)),
      // summa summarum, trying to make this work otherwise requires messing around with hot observables and is quite complicated to make work
      tap(() => this.loading = false)
    );

    return tableData$;
  }

  private getTagsSource() {
    return from(this.tags).pipe(
      rateLimit(1, this.RATE_LIMIT_MS),
      map((tagPair): RequestedTag => {
        return {
          mainTag: tagPair[0],
          subTag: tagPair[1],
          fullTag: tagPair[0] + ':' + tagPair[1]
        }
      }),
      concatMap(async (requestedTag): Promise<WrappedTagsResult> => {
        try {
          const tagsResult = await this.valmetDna.getTags(requestedTag.mainTag);
          const tagIndex = tagsResult.Tag.map(tag => tag.TagName).indexOf(requestedTag.fullTag);
          const tagFound = tagIndex !== -1;
          const tag = tagFound ? tagsResult.Tag[tagIndex] : null;

          return {
            tagsResult,
            requestedTag,
            tagFound,
            tag
          }
        } catch(err) {
          return {
            tagsResult: null,
            requestedTag,
            tagFound: false,
            tag: null
          }
        }
      }),
      reduce((acc: WrappedTagsResult[], value) => acc.concat(value), [])
    );
  }

  private getValuesSource(wrappedTagsResults: WrappedTagsResult[]) {
    const period = '1';
    const getFromDate = () => {
      const now = new Date();
      const fromDate = new Date(now.getTime() - (+period * 60 * 1000));
      fromDate.setUTCSeconds(0, 0);
      return fromDate;
    }

    return from(wrappedTagsResults).pipe(
      rateLimit(1, this.RATE_LIMIT_MS),
      concatMap(async wrappedTagsResult => {
        const errorObject = {
          tagsResult: wrappedTagsResult,
          trendResult: undefined
        }

        // if tag was not found, no sense to query trends
        if(!wrappedTagsResult.tagFound) {
          return errorObject;
        }

        try {
          const trendResult = await this.valmetDna.getTrend(wrappedTagsResult.requestedTag.mainTag, wrappedTagsResult.requestedTag.subTag, {
            from: getFromDate().toISOString(),
            period
          });

          return {
            tagsResult: wrappedTagsResult,
            trendResult
          }
        } catch(err) {
          return errorObject;
        }
      }),
      reduce((acc: CombinedResults[], value) => acc.concat(value), [])
    );
  }

  private getTableData(results: CombinedResults[]): TableData {
    // do in template instead?
    const formatValueToTwoDecimals = (value: number) => value.toFixed(2);

    const nonNullValues = this.getLastValueResults(results)
    // ensure null values do not affect the aggregate sum
      .map(result => result.trendResult?.Values[0]?.value ?? null)
      .filter(value => value !== null)


    const totalValueSum = nonNullValues.length > 0 ?
      nonNullValues
        .map(strValue => +strValue)
        .reduce((prev, curr) => prev + curr)
      : undefined;
    ;

    const tableData: TableData = {
      rows: results.map(result => {
        const name = result.tagsResult.requestedTag.fullTag;

        if(!result.trendResult) {
          return {
            name,
            formattedValue: undefined,
            valmetDnaValue: undefined,
            valmetDnaWrappedTagsResult: result.tagsResult
          };
        }

        const hasValue = !!result.trendResult.Values[0];
        const lastTrendValue = result.trendResult.Values[0] ?? null;

        const formatValue = (value: number) => {
          // FIXME: for demo, total sum should be fixed to work with negative numbers as well
          if(result.tagsResult.requestedTag.fullTag === 'NDE11FU001:av') {
            return '-' + formatValueToTwoDecimals(value);
          }
          return formatValueToTwoDecimals(value);
        };

        return {
          name,
          formattedValue: hasValue ? formatValue(+lastTrendValue.value) : undefined,
          valmetDnaValue: lastTrendValue,
          valmetDnaWrappedTagsResult: result.tagsResult
        };
      }),
      totalValueSum: totalValueSum ? formatValueToTwoDecimals(totalValueSum) : undefined
    }
    return tableData;
  }

  /**
   * Filters all other result values except the last (newest)
   *
   */
  private getLastValueResults(results: CombinedResults[]) {
    return results.map(result => {
      const noValuesExist = result.trendResult?.Values.length === 0;
      if(noValuesExist) return result;

      // ensure there is always exactly one value for calculations
      const lastValueOnlyResult = Object.assign({}, result);
      const lastTrendValue = result.trendResult.Values[result.trendResult.Values.length - 1];
      lastValueOnlyResult.trendResult.Values = [lastTrendValue];
      return lastValueOnlyResult;
    })
  }

}
